import Layout from "components/layout";
import Theme from "components/template/Theme";
import appConfig from "configs/app.config";
import { useEffect } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { PersistGate } from "redux-persist/integration/react";
import { pushData } from "store/parc/parcSlice";
import API from "utils/axiosInstance";
import { Button } from "./components/ui";
import history from "./history";
import "./locales";
import mockServer from "./mock";
import store, { persistor } from "./store";
import useAuth from "./utils/hooks/useAuth";

const { apiPrefix } = appConfig;

const environment = process.env.NODE_ENV;
const isMaintenance = process.env.REACT_APP_MAINTENANCE === "true"; // 👈 variable env

if (environment !== "production" && appConfig.enableMock) {
  mockServer({ environment });
}

function App() {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <BrowserRouter history={history}>
          <Theme>{isMaintenance ? <MaintenancePage /> : <AppContent />}</Theme>
        </BrowserRouter>
      </PersistGate>
    </Provider>
  );
}

function AppContent() {
  const isEmpty = useSelector((state) => state.auth.user.isEmpty);
  const dispatch = useDispatch();
  const agencyDefault = useSelector(
    (state) => state.auth.user.defaultAgency.id,
  );

  const fetchBookingWithoutVehicle = async () => {
    const config = {
      page: 1,
      itemPerPage: 10,
      agencyId: agencyDefault,
    };

    const response = await API.post(
      `${apiPrefix}/bookings/withoutVehicle`,
      config,
    );
    if (response.data.data && response.data.pagination.totalItems > 0) {
      dispatch(
        pushData({
          booking: response.data.pagination.totalItems,
        }),
      );
    } else {
      dispatch(
        pushData({
          booking: 0,
        }),
      );
    }
  };

  const fetchBookingEstimateDepositAlert = async () => {
    const response = await API.get(
      `${apiPrefix}/bookings/estimate-deposit-alert/${agencyDefault}`,
    );
    if (response.data.alert && response.data.count > 0) {
      dispatch(
        pushData({
          estimateDepositAlert: response.data.count,
          estimateDepositBookings: response.data.bookings || [],
        }),
      );
    } else {
      dispatch(
        pushData({
          estimateDepositAlert: 0,
          estimateDepositBookings: [],
        }),
      );
    }
  };

  useEffect(() => {
    if (agencyDefault) {
      fetchBookingWithoutVehicle();
      fetchBookingEstimateDepositAlert();
    }
  }, [agencyDefault]);

  if (isEmpty === null) return <Layout />;

  return isEmpty === false ? <Layout /> : <NoAgencyAssigned />;
}

const NoAgencyAssigned = () => {
  const { signOut } = useAuth();
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-6 text-center">
      <div className="bg-white shadow-lg rounded-2xl p-6 max-w-md">
        <h1 className="text-2xl font-semibold text-gray-800 mb-4">
          Accès en attente 🚧
        </h1>
        <p className="text-gray-600">
          Vous êtes bien connecté, mais aucune agence ne vous a encore été
          attribuée. Un supérieur hiérarchique doit valider votre accès avant
          que vous puissiez consulter les données.
        </p>
        <img
          src="https://media.giphy.com/media/3o7aD2saalBwwftBIY/giphy.gif"
          alt="Waiting GIF"
          className="w-64 h-auto mx-auto my-4 rounded-lg"
        />
        <p className="text-gray-500">
          Merci de patienter ou de contacter votre responsable. ⏳
        </p>
        <Button onClick={signOut} variant={"solid"} block className={"mt-4"}>
          Se déconnecter
        </Button>
      </div>
    </div>
  );
};

// 👇 Page de maintenance
const MaintenancePage = () => {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-6 text-center">
      <div className="bg-white shadow-lg rounded-2xl p-6 max-w-md">
        <h1 className="text-3xl font-bold text-gray-800 mb-4">
          🚧 Site en maintenance 🚧
        </h1>
        <p className="text-gray-600">
          Nous procédons actuellement à une mise à jour de la plateforme. Merci
          de revenir un peu plus tard ⏳
        </p>
        <img
          src="https://media.giphy.com/media/3oEjI6SIIHBdRxXI40/giphy.gif"
          alt="Maintenance GIF"
          className="w-64 h-auto mx-auto my-4 rounded-lg"
        />
        <p className="text-gray-500">— L’équipe technique</p>
      </div>
    </div>
  );
};

export default App;
